import { MODULE_CATALOG, isCardModuleEnabled, visibleLinksFor } from './moduleCatalog';
import { FEATURE_ACTIONS } from './featureActions';
import { ModuleKey } from '../dashboard/registry/types';

/** One page the header search can open. */
export interface FeatureResult {
    label: string;
    path: string;
    /** Module the page belongs to, or the launcher group for a module's own entry. */
    module: string;
    emoji?: string;
    /** Extra words that match but are not shown, e.g. "new" and "add" for a "Create ..." action. */
    keywords?: string[];
    /** In-page task: tip shown on arrival and the `data-help` keys of the control to point at. */
    hint?: string;
    targets?: string[];
}

/**
 * One control from the help catalogue the search can point at (generated into helpSearchIndex.json):
 * help key, title, the page that shows it, whether a record must be opened first, and the
 * permission keys it needs (any one, or every one when `a`).
 */
export interface HelpIndexEntry { k: string; t: string; p: string; o?: true; n?: string[]; a?: true }

/** At most this many in-page controls, listed after the pages and actions. */
const CONTROL_LIMIT = 4;

/** Words people use for the same thing; any word of a group also finds the others. */
const SYNONYMS = [
    ['create', 'new', 'add'],
    ['password', 'pwd', 'pin', 'passcode'],
    ['reset', 'forgot', 'lost', 'locked'],
    ['deactivate', 'disable', 'block', 'suspend'],
    ['delete', 'remove'],
    ['change', 'edit', 'update', 'modify'],
    ['approve', 'accept', 'authorise', 'authorize'],
    ['download', 'print', 'pdf'],
    ['clock', 'check', 'punch'],
    ['payslip', 'salary', 'pay'],
];
const synonymsOf = (word: string) => SYNONYMS.filter(group => group.includes(word)).flat();

const words = (text: string) => text.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

/**
 * How well `entry` matches the query, lower is better; null when it does not match.
 * 0: the name starts with the query. 1: every query word starts a word of the name.
 * 2: every query word starts a word of the name or its module, or sits inside the name.
 */
const rank = (entry: FeatureResult, query: string, tokens: string[]): number | null => {
    const label = entry.label.toLowerCase();
    if (label.startsWith(query)) return 0;
    // Hidden words need two letters, so "u" finds Users but not every action with an "update" synonym.
    const labelWords = [...words(entry.label), ...(query.length >= 2 ? entry.keywords ?? [] : [])];
    if (tokens.every(t => labelWords.some(w => w.startsWith(t)))) return 1;
    const allWords = [...labelWords, ...words(entry.module)];
    if (tokens.every(t => allWords.some(w => w.startsWith(t)) || label.includes(t))) return 2;
    return null;
};

/**
 * Pages the signed-in user can open, and actions they can take, whose name (or module name) matches
 * `query`, best first. Built from the same catalog, visibility map and module switches as the
 * sidebar, so search never offers a page the sidebar would hide. Actions also need `can` to pass
 * their permission; without `can` no actions are offered.
 */
export const searchFeatures = (
    query: string,
    visibility: Record<string, boolean>,
    enabledModules: Set<ModuleKey>,
    limit = 8,
    can?: (key: string) => boolean,
    controls?: HelpIndexEntry[],
): FeatureResult[] => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const tokens = words(q);
    if (tokens.length === 0) return [];

    const entries: FeatureResult[] = [];
    const seen = new Set<string>();
    const add = (entry: FeatureResult) => {
        if (seen.has(entry.path)) return;
        seen.add(entry.path);
        entries.push(entry);
    };

    const cards = MODULE_CATALOG.filter(card => visibility[card.key] === true && isCardModuleEnabled(card, enabledModules));
    const linksByCard = cards.map(card => visibleLinksFor(card, visibility));
    // Pages first, so a page that shares the module's landing path keeps its own, more exact name.
    // A link named after another module ("Payroll" under Dashboard) is left out so that module shows once.
    const cardByLabel = new Map(cards.map(card => [card.label.toLowerCase(), card.key]));
    cards.forEach((card, i) => linksByCard[i].forEach(link => {
        const named = cardByLabel.get(link.label.toLowerCase());
        if (named === undefined || named === card.key) add({ label: link.label, path: link.path, module: card.label, emoji: link.emoji });
    }));
    cards.forEach((card, i) => {
        const links = linksByCard[i];
        // Same target as the breadcrumbs: the module page when it has several pages, else its one page.
        const path = links.length > 1 ? `/modules/${card.key}` : (links[0]?.path ?? card.path);
        add({ label: card.label, path, module: card.group, emoji: card.nav?.emoji });
    });
    if (can) {
        const cardByKey = new Map(cards.map(card => [card.key, card]));
        FEATURE_ACTIONS.forEach(action => {
            const card = action.card === undefined ? undefined : cardByKey.get(action.card);
            if ((action.card !== undefined && !card) || (action.gate && visibility[action.gate] !== true)) return;
            const keys = action.permission === undefined ? [] : ([] as string[]).concat(action.permission);
            if (keys.length > 0 && !keys.some(can)) return;
            const own = [...words(action.label), ...(action.keywords ?? [])];
            entries.push({
                label: action.label, path: action.path, module: `Action · ${card?.label ?? action.module}`, emoji: action.emoji,
                keywords: [...(action.keywords ?? []), ...own.flatMap(synonymsOf)],
                hint: action.hint, targets: action.targets,
            });
        });
    }

    const best = (list: FeatureResult[], maxScore: number, max: number) => list
        .map(entry => ({ entry, score: rank(entry, q, tokens) }))
        .filter((r): r is { entry: FeatureResult; score: number } => r.score !== null && r.score <= maxScore)
        .sort((a, b) => a.score - b.score || a.entry.label.length - b.entry.label.length
            || a.entry.label.localeCompare(b.entry.label))
        .slice(0, max)
        .map(r => r.entry);
    const found = best(entries, 2, limit);
    if (!controls || !can || q.length < 3) return found;

    // In-page controls: only on a page the user can open, with the control's own permission, and
    // not one an action above already points at. Titles only, so "post" does not list every page.
    const pages = [...linksByCard.flat(), ...cards]
        .map(page => ({ label: page.label, path: page.path.split('?')[0] }))
        .sort((a, b) => b.path.length - a.path.length);
    const pointed = new Set(entries.flatMap(e => e.targets ?? []));
    const shown = new Set(found.map(e => `${e.label.toLowerCase()}|${e.path}`));
    const onPage: FeatureResult[] = [];
    controls.forEach(c => {
        if (pointed.has(c.k) || (c.n?.length && !(c.a ? c.n.every(can) : c.n.some(can)))) return;
        const page = pages.find(l => c.p === l.path || c.p.startsWith(`${l.path}/`));
        if (!page || shown.has(`${c.t.toLowerCase()}|${c.p}`)) return;
        onPage.push({
            label: c.t, path: c.p, module: `On page · ${page.label}`, emoji: '👉',
            keywords: words(c.t).flatMap(synonymsOf),
            hint: c.o ? `Open a record, then look for "${c.t}".` : `Look for "${c.t}".`, targets: [c.k],
        });
    });
    return [...found, ...best(onPage, 1, CONTROL_LIMIT)];
};
